import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { formatUnits, type Address } from "viem";
import { REDES, type RedKeel } from "../config/networks";
import { useMiReferido, usePanelReferidor, useAceptarInvitacion } from "../hooks/useReferidos";
import { enlaceInvitacion } from "../lib/referido";
import { corto } from "../lib/formato";
import { Giro } from "./Modal";
import { useT } from "../lib/i18n";

/**
 * Invitar y cobrar por ello.
 *
 * LO QUE ESTA PANTALLA TIENE QUE DEJAR CLARO EN LOS PRIMEROS DIEZ SEGUNDOS, porque es lo que decide
 * si alguien comparte su enlace o no:
 *
 *   1. A QUIEN INVITAS NO LE CUESTA NADA. Sigue pagando el mismo 10% de sus comisiones que pagaria
 *      si hubiera entrado solo. Lo que se reparte es la parte de Keellabs. Si esto no queda dicho,
 *      compartir el enlace se siente como venderle algo peor a un amigo, y nadie lo hace.
 *   2. NO SE COBRA DESDE EL PRIMER DIA. Hasta que el invitado no genera un minimo de comisiones, el
 *      referido no paga. Decirlo aqui, antes de que alguien mire su panel a cero y crea que esta
 *      roto, es la diferencia entre una regla y un fallo aparente.
 *   3. QUE SE COBRA SOLO. El reparto lo hace el contrato en el mismo cobro; no hay que reclamar
 *      nada, ni escribirnos, ni esperar a que alguien pague a mano.
 */
export function Referidos({ red, alVolver }: { red: RedKeel; alVolver: () => void }) {
  const t = useT();
  const { address } = useAccount();
  const { data: mio } = useMiReferido(red);
  const panel = usePanelReferidor();
  const [copiado, setCopiado] = useState(false);

  const enlace = address ? enlaceInvitacion(address) : "";
  const texto = t("Estoy usando Keellabs para poner a trabajar mi dinero en piscinas de liquidez. Si entras por aquí, entras igual que yo:");

  async function copiar() {
    try {
      await navigator.clipboard.writeText(enlace);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sin permiso de portapapeles el campo sigue en pantalla y se puede seleccionar a mano.
    }
  }

  const abrir = (u: string) => window.open(u, "_blank", "noopener,noreferrer");

  return (
    <section className="tarjeta">
      <button className="btn fantasma" onClick={alVolver}>{t("← Volver")}</button>
      <div className="mod-titulo" style={{ marginTop: 8 }}>
        <span className="mod-ico" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="8" width="18" height="4" rx="1" /><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8" />
            <path d="M12 8v13" /><path d="M12 8S9.5 3 7 4.5 9 8 12 8zm0 0s2.5-5 5-3.5S15 8 12 8z" />
          </svg>
        </span>
        <h2 style={{ margin: 0 }}>{t("Invita y cobra")}</h2>
      </div>

      <p className="detalle">
        {t("Quien entre por tu enlace")} <strong>{t("paga exactamente lo mismo")}</strong> {t("que si hubiera entrado solo: el 10% de las comisiones que gane su piscina, nunca de su capital. Lo que se reparte es")}{" "}
        <strong>{t("nuestra")}</strong> {t("parte.")}
      </p>

      {/* EL REPARTO, EN CIFRAS Y NO EN UNA FRASE. Es el dato que decide si alguien comparte el
          enlace, y enterrado en un párrafo hay que leerlo dos veces para saber cuánto se lleva. */}
      <div className="reparto-ref">
        <div className="tuyo">
          <div className="n">2%</div>
          <div className="q">{t("para ti")}</div>
          <div className="s">{t("de las comisiones que genere tu invitado")}</div>
        </div>
        <div>
          <div className="n">8%</div>
          <div className="q">{t("para Keellabs")}</div>
          <div className="s">{t("lo que antes era el 10% entero")}</div>
        </div>
        <div>
          <div className="n">0</div>
          <div className="q">{t("le cuesta a él")}</div>
          <div className="s">{t("paga lo mismo, con invitación o sin ella")}</div>
        </div>
      </div>

      {/* EL ENLACE SE VE ENTERO. En un `input` de una línea se cortaba por la mitad y no había forma
          de comprobar a simple vista que la dirección del final es la tuya — que es justo lo único
          que hay que mirar antes de mandárselo a alguien. */}
      <div className="caja-enlace">
        <div className="et">{t("Tu enlace de invitación")}</div>
        <code className="enlace" onClick={(e) => {
          // Un clic selecciona el enlace entero: en el móvil, arrastrar para seleccionar texto
          // dentro de una tarjeta es justo lo que no se consigue a la primera.
          const r = document.createRange();
          r.selectNodeContents(e.currentTarget);
          const s = getSelection();
          s?.removeAllRanges();
          s?.addRange(r);
        }}>
          {enlace || t("conecta tu wallet para verlo")}
        </code>
        <div className="acciones">
          <button className="btn principal" onClick={copiar} disabled={!enlace}>
            {copiado ? t("Copiado ✓") : t("Copiar")}
          </button>
          <button className="btn wasap" disabled={!enlace}
            onClick={() => abrir(`https://wa.me/?text=${encodeURIComponent(`${texto} ${enlace}`)}`)}>
            WhatsApp
          </button>
          <button className="btn tele" disabled={!enlace}
            onClick={() => abrir(`https://t.me/share/url?url=${encodeURIComponent(enlace)}&text=${encodeURIComponent(texto)}`)}>
            Telegram
          </button>
        </div>
      </div>

      <p className="detalle">
        {t("Se cobra")} <strong>{t("solo")}</strong>{t(": cada vez que la piscina de tu invitado cobra comisiones, tu parte sale en esa misma transacción y llega a tu wallet. No hay nada que reclamar.")}{" "}
        <strong>{t("Eso sí")}</strong>{t(": no empieza el primer día. Tu invitado tiene que haber generado un mínimo de comisiones antes de que el reparto se active — es lo que evita que alguien abra dos wallets y se invite a sí mismo.")}
      </p>

      {!mio?.hayRegistro && (
        <div className="aviso">
          <p className="detalle">
            {t("En {etiqueta} el reparto", { etiqueta: red.etiqueta })} <strong>{t("todavía no está activo")}</strong>{t(": el registro no está desplegado en esta red. Tu enlace ya funciona y las altas quedan guardadas, así que quien entre ahora cuenta cuando se encienda.")}
          </p>
        </div>
      )}

      <h3 style={{ marginTop: 26 }}>{t("Lo que llevas ganado")}</h3>
      {panel.isLoading && <Giro texto={t("Contando…")} />}
      {panel.isError && (
        <div className="aviso"><p className="detalle">
          {t("No pude leer tus números ahora mismo — reinténtalo en un momento. Tu enlace y el reparto no dependen de esto.")}
        </p></div>
      )}
      {panel.data && (
        <>
          <div className="cifras">
            <div>
              <div className="k">{t("Invitados")}</div>
              <div className="v">{panel.data.invitados}</div>
              <div className="s">{t("wallets que entraron por tu enlace")}</div>
            </div>
            <div>
              <div className="k">{t("Ya te pagan")}</div>
              <div className="v">{panel.data.activos}</div>
              <div className="s">{panel.data.invitados > 0
                ? t("de {n} · pasaron el mínimo", { n: panel.data.invitados })
                : t("cuando pasen el mínimo de comisiones")}</div>
            </div>
          </div>

          {panel.data.invitados === 0 ? (
            <p className="detalle ref-vacio">
              {t("Aún no ha entrado nadie por tu enlace. Compártelo 👆 — cobras el")} <strong>2%</strong> {t("de lo que generen sus piscinas, y a ellos no les cuesta nada.")}
            </p>
          ) : (
            <>
              {/* Primero lo que YA es tuyo (en verde, es la cifra que se viene a ver), luego la base
                  sobre la que se calcula. */}
              <Importes titulo={t("Ya cobrado · en tu wallet")} datos={panel.data.pagado} tono="cobrado"
                vacio={t("Todavía nada cobrado. Se paga solo, en cuanto la piscina de un invitado cobre comisiones.")} />
              <Importes titulo={t("Han generado tus invitados")} datos={panel.data.generado}
                vacio={t("Tus invitados aún no han generado comisiones.")} />
              <PorInvitado lista={panel.data.porInvitado} />
            </>
          )}
        </>
      )}

      {(mio?.referidorEnCadena || mio?.referidorEnServicio) && (
        <p className="detalle" style={{ marginTop: 20 }}>
          {t("A ti te invitó")} <code>{corto(mio.referidorEnCadena ?? mio.referidorEnServicio ?? undefined)}</code>
          {mio.activo ? t(" y ya está cobrando su parte de nuestra comisión.") : t(", y empezará a cobrar cuando tus piscinas hayan generado el mínimo.")}{" "}
          {t("A ti no te cuesta nada: pagas lo mismo que pagarías sin él.")}
        </p>
      )}
    </section>
  );
}

/**
 * El banner de "te han invitado". Vive donde se mira primero, no escondido en una pantalla aparte:
 * quien llega por un enlace no sabe todavia que existe la pantalla de referidos.
 *
 * PIDE UNA FIRMA, NO UNA TRANSACCION. Aceptar la invitacion no puede costarle gas a alguien que
 * acaba de llegar y todavia no ha puesto un euro.
 */
export function AvisoInvitacion({ red }: { red: RedKeel | undefined }) {
  const t = useT();
  const { data } = useMiReferido(red);
  const acc = useAceptarInvitacion(red);

  const yaTiene = !!(data?.referidorEnCadena || data?.referidorEnServicio);
  if (!data?.pendiente || yaTiene) return null;

  if (acc.hecho) {
    return (
      <div className="aviso">
        <p className="detalle">
          {t("Invitación aceptada. A ti no te cambia nada: pagas la misma comisión de siempre.")}
        </p>
      </div>
    );
  }

  return (
    <div className="aviso">
      <p className="detalle">
        {t("Te invitó")} <code>{corto(data.pendiente)}</code>. {t("Si lo aceptas, se lleva una parte de")}{" "}
        <strong>{t("nuestra")}</strong> {t("comisión cuando tus piscinas ganen —")}{" "}
        <strong>{t("tú pagas exactamente lo mismo")}</strong>{t(", con invitación o sin ella.")}
      </p>
      {acc.error && <p className="detalle">{acc.error}</p>}
      <div className="acciones">
        <button className="btn principal" disabled={!!acc.haciendo}
          onClick={() => acc.aceptar(data.pendiente!)}>
          {acc.haciendo ? <Giro /> : t("Aceptar (solo una firma, sin gas)")}
        </button>
        <button className="btn fantasma" disabled={!!acc.haciendo} onClick={() => acc.rechazar()}>
          {t("No, gracias")}
        </button>
      </div>
    </div>
  );
}

/**
 * Importes por red y por token. SE LEEN EL SIMBOLO Y LOS DECIMALES DE CADA TOKEN, no se asumen: un
 * USDC de 6 decimales pintado como si tuviera 18 enseña 0,000000012 donde hay 12 dólares, y nadie
 * lee eso como un error de formato — lo lee como que no ha ganado nada.
 */
/**
 * Desglose POR INVITADO con su WALLET: la lista de las direcciones que entraron por tu enlace,
 * cuánto generó cada una y si ya cuenta. El grafo de referidos ya es público on-chain, así que
 * mostrarte tus propios invitados aquí no expone nada nuevo.
 */
function PorInvitado({ lista }: {
  lista?: { wallet: Address; activo: boolean; generado: Record<string, Record<string, string>> }[];
}) {
  const t = useT();
  const config = useConfig();
  const pares = (lista ?? []).flatMap((inv) =>
    Object.entries(inv.generado).flatMap(([red, porToken]) =>
      Object.keys(porToken).map((token) => ({ red, token: token as Address }))));
  const info = useQuery({
    queryKey: ["tokens-invitados", pares.map((p) => `${p.red}:${p.token}`).join(",")],
    enabled: pares.length > 0,
    staleTime: 3_600_000,
    queryFn: async () => {
      const abi = [
        { type: "function", name: "symbol", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
        { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
      ] as const;
      const salida: Record<string, { simbolo: string; decimales: number }> = {};
      for (const p of pares) {
        const cadena = REDES[p.red as keyof typeof REDES]?.idCadena;
        if (!cadena) continue;
        const cliente = getPublicClient(config, { chainId: cadena });
        if (!cliente) continue;
        try {
          const [simbolo, decimales] = await cliente.multicall({
            allowFailure: false,
            contracts: [
              { address: p.token, abi, functionName: "symbol" },
              { address: p.token, abi, functionName: "decimals" },
            ],
          });
          salida[`${p.red}:${p.token}`] = { simbolo: simbolo as string, decimales: Number(decimales) };
        } catch { /* token sin nombre: se omite */ }
      }
      return salida;
    },
  });

  if (!lista || !lista.length) return null;

  const fmt = (gen: Record<string, Record<string, string>>) => {
    const partes = Object.entries(gen).flatMap(([red, porToken]) =>
      Object.entries(porToken).map(([tok, imp]) => {
        const i = info.data?.[`${red}:${tok}`];
        return i ? `${formatUnits(BigInt(imp), i.decimales)} ${i.simbolo}` : "…";
      }));
    return partes.length ? partes.join(" + ") : t("aún nada");
  };

  const corta = (w: string) => `${w.slice(0, 6)}…${w.slice(-4)}`;
  const copiar = (w: string) => { navigator.clipboard?.writeText(w).catch(() => {}); };

  return (
    <div className="ref-invitados" style={{ marginTop: 14 }}>
      <div className="et">{t("Tus invitados · {n} {palabra}", { n: lista.length, palabra: lista.length === 1 ? t("wallet") : t("wallets") })}</div>
      <ul>
        {lista.map((inv, n) => (
          <li key={inv.wallet ?? n}>
            <span className="quien">
              <button
                className="ref-wallet"
                title={t("{wallet} · copiar", { wallet: inv.wallet })}
                onClick={() => copiar(inv.wallet)}
              >
                {corta(inv.wallet)}
              </button>
              <span className={inv.activo ? "marca-ok" : "marca-pend"}>
                {inv.activo ? t("ya cuenta") : t("pendiente")}
              </span>
            </span>
            <span className="cuanto">{fmt(inv.generado)}</span>
          </li>
        ))}
      </ul>
      <p className="detalle" style={{ margin: "10px 0 0" }}>
        {t("Son las wallets que se dieron de alta con tu enlace. Toca una para copiarla.")}
      </p>
    </div>
  );
}

function Importes({ titulo, datos, tono, vacio }: {
  titulo: string; datos: Record<string, Record<string, string>>;
  tono?: "cobrado"; vacio?: string;
}) {
  const config = useConfig();
  const pares = Object.entries(datos ?? {}).flatMap(([red, porToken]) =>
    Object.entries(porToken).map(([token, importe]) => ({ red, token: token as Address, importe }))
  );

  const info = useQuery({
    queryKey: ["tokens-referidos", pares.map((p) => `${p.red}:${p.token}`).join(",")],
    enabled: pares.length > 0,
    staleTime: 3_600_000,
    queryFn: async () => {
      const abi = [
        { type: "function", name: "symbol", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
        { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
      ] as const;
      const salida: Record<string, { simbolo: string; decimales: number }> = {};
      for (const p of pares) {
        const cadena = REDES[p.red as keyof typeof REDES]?.idCadena;
        if (!cadena) continue;
        const cliente = getPublicClient(config, { chainId: cadena });
        if (!cliente) continue;
        try {
          const [simbolo, decimales] = await cliente.multicall({
            allowFailure: false,
            contracts: [
              { address: p.token, abi, functionName: "symbol" },
              { address: p.token, abi, functionName: "decimals" },
            ],
          });
          salida[`${p.red}:${p.token}`] = { simbolo: simbolo as string, decimales: Number(decimales) };
        } catch {
          // Un token que no contesta se queda sin nombre, y entonces no se pinta: mas vale una
          // linea de menos que una cifra con las comas donde no van.
        }
      }
      return salida;
    },
  });

  // Estado vacío RESUELTO: en vez de no pintar nada (que deja al usuario sin saber si es 0 o si
  // falló), se muestra el título con una línea que explica por qué está a cero.
  if (!pares.length) {
    return (
      <div className="resumen-crear ref-importes" style={{ marginTop: 14 }}>
        <div><span className="k"><strong>{titulo}</strong></span><span className="v" /></div>
        {vacio && <div><span className="k" style={{ color: "var(--sonda)", fontWeight: 400 }}>{vacio}</span><span className="v" /></div>}
      </div>
    );
  }

  return (
    <div className={`resumen-crear ref-importes ${tono === "cobrado" ? "cobrado" : ""}`} style={{ marginTop: 14 }}>
      <div><span className="k"><strong>{titulo}</strong></span><span className="v" /></div>
      {pares.map((p) => {
        const i = info.data?.[`${p.red}:${p.token}`];
        return (
          <div key={`${p.red}:${p.token}`}>
            <span className="k">{REDES[p.red as keyof typeof REDES]?.etiqueta ?? p.red}</span>
            <span className="v">
              {i ? `${formatUnits(BigInt(p.importe), i.decimales)} ${i.simbolo}` : "…"}
            </span>
          </div>
        );
      })}
    </div>
  );
}
